import {
  sleep,
  getPlantLastModifyByIcon,
  getPlantLastModifyByLabel,
  getPlantIconImageUrlsByType,
  getUserInitialsForBubble,
} from '@/core/utils/utils';
import { mapGetters } from 'vuex';
import plantsClient from '@/core/services/plants';
import { Vue, Options } from 'vue-class-component';
import {
  PlantStatus,
  PlantListDTO,
  PlantDetailDTO,
  PlantType,
  PlantTagDTO,
} from '@/../client-generator/generated-clients/plants';
import { SET_SELECTED_PLANT } from '@/core/store/action-types';

@Options({
  name: 'PlantCard',
  props: {
    plant: {
      required: true,
      type: Object as () => PlantListDTO,
    },
  },
  emits: ['duplicate', 'duplicate-full', 'edit', 'delete'],
  watch: {
    'plant.justSynchronized': function (newValue) {
      if (newValue) {
        this.$nextTick(() => {
          this.handleDisplayOverlay();
        });
      }
    },
  },
  computed: {
    ...mapGetters('UI', ['plantEditListEnabled', 'plantDeleteListEnabled']),
  },
})
export default class PlantCard extends Vue {
  plant!: PlantListDTO;
  currentPlant!: PlantDetailDTO;
  plantEditListEnabled!: boolean;
  plantDeleteListEnabled!: boolean;

  isLoading = false;
  doShowOverlay = false;

  get iconStatus() {
    let iconStatus = 'icon-edit-page';

    switch (this.plant.status) {
      case PlantStatus.PROJECT:
        iconStatus = 'icon-edit-page';
        break;
      case PlantStatus.COMMISSIONED:
        iconStatus = 'icon-user';
        break;
      case PlantStatus.INSTALLED:
        iconStatus = 'icon-staff';
        break;
    }

    return iconStatus;
  }

  get computePlantAddress(): string {
    return `${this.plant.address}, ${this.plant.country}`;
  }

  get iconDevice() {
    return getPlantLastModifyByIcon(this.plant.lastModifyByDesktop);
  }

  get iconDeviceLabel() {
    return getPlantLastModifyByLabel(this.plant.lastModifyByDesktop);
  }

  get computedTags() {
    const tags: { list: PlantTagDTO[]; extraTags: number | string } = {list: [], extraTags: 0};
    if (!this.plant.tags) return tags;

    tags.list = this.plant.tags.slice(0, 3);
    const remainderTags = this.plant.tags.length - tags.list.length;
    tags.extraTags = remainderTags > 0 ? `+ ${remainderTags}` : 0;

    return tags;
  }

  get username() {
    return `${this.plant.lastModifyBy?.name} ${this.plant.lastModifyBy?.surname}`;
  }

  get userInitials() {
    return getUserInitialsForBubble(this.plant.lastModifyBy);
  }

  get status() {
    if (this.plant.status === undefined) return '';
    return this.$t(`plants.status.${PlantStatus[this.plant.status]}`);
  }

  get imageURLs() {
    return getPlantIconImageUrlsByType(
      this.plant.plantTypeIcon,
      this.plant.customIconImageUrl,
      this.plant.plantType
    );
  }

  get imageSrcSetURLs() {
    if (this.imageURLs.length <= 1) return this.imageURLs[0];
    return `${this.imageURLs[0]}, ${this.imageURLs[1]} 2x, ${this.imageURLs[2]} 3x`;
  }

  get formattedUpdateDate() {
    return this.plant.lastModify?.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  }

  get favouriteIcon() {
    return require(`@/assets/images/icons/icon-favourite${
      !this.plant.isFavourite ? '-outline' : ''
    }.svg`);
  }

  get isSessionLocked() {
    return this.plant.sessionLocked;
  }

  get sessionLockedByName() {
    return `${this.plant.sessionLockedBy?.name} ${this.plant.sessionLockedBy?.surname}`;
  }

  get showOverlay() {
    return (
      (this.plant.justSynchronized && this.doShowOverlay) ||
      this.isSessionLocked ||
      this.isLoading
    );
  }

  get overlayIcon() {
    let icon = '';

    if (this.plant.justSynchronized || this.isLoading) {
      icon = 'icon-sync';
    }

    return icon;
  }

  get showDuplicate() {
    return this.plant.plantType === PlantType.AUTOMATION_PLANT;
  }

  handleEdit() {
    this.$emit('edit');
  }

  handleDelete() {
    if (!this.plantDeleteListEnabled || !this.plant.canBeDeleted) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
      return;
    }
    this.$emit('delete');
  }

  handleDuplicateCard() {
    this.$emit('duplicate');
  }

  handleDuplicateCardFull() {
    this.$emit('duplicate-full');
  }

  async handleDisplayOverlay() {
    this.doShowOverlay = true;
    await sleep(2000);
    this.doShowOverlay = false;
  }

  async togglePlantFromFavorites() {
    if (!this.plant.desktopId) return;
    try {
      this.isLoading = true;
      this.plant.isFavourite = await plantsClient.togglePlantFromFavorites(
        this.plant.desktopId
      );
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.isLoading = false;
    }
  }

  handleGoToPlantDetail() {
    if (this.isSessionLocked) return;

    let pageName: String = '';
    switch (this.plant.plantType) {
      case PlantType.AUTOMATION_PLANT:
        pageName = 'page-plant-detail';
        break;
      case PlantType.ENTRANCE_PLANT:
        pageName = 'page-entranceplant-detail';
    }

    this.$router.push({
      name: `${pageName}`,
      params: {
        plantId: this.plant.desktopId?.toString() ?? '',
      },
    });
  }

  handleOptionsClick(e: MouseEvent) {
    this.$store.dispatch(SET_SELECTED_PLANT, this.plant);
  }
}
